import { API } from 'aws-amplify';

// CP-857: data layer for the TenStreet code table screens.
//
// These tables live in the brand's UltraStaff database, not DynamoDB, so they go over the
// dqf-apiv2 REST API rather than AppSync. The 'centerlineportal' endpoint is configured in
// src/App.js and attaches the Cognito ID token automatically.
//
// Centerline only: the data is TenStreet, which PRST does not have. The routes and the
// Admin Portal buttons are brand-gated, so these are never called from a PRST build.
const API_NAME = 'centerlineportal';

// The API rejects invalid writes with a 400 and a user-readable message from the stored
// procedure ("Worklist \"X\" is already mapped. Edit that row instead."). Surface that
// rather than a generic failure, since it tells the user exactly what to fix. A successful
// write can also carry advisory notes on its message -- the proc appends warnings such as
// "TenStreet has never sent this worklist" -- which is why the callers show it too.
const messageFrom = (e, fallback) => e?.response?.data?.response_message?.message || e?.message || fallback;

export const listRows = async config => {
  try {
    const response = await API.get(API_NAME, config.endpoint);
    return response?.response_body ?? [];
  } catch (e) {
    console.log('listRows error', e);
    throw new Error(messageFrom(e, `Failed to load ${config.title}. Please try again later.`));
  }
};

/**
 * The values TenStreet has actually sent, for the "never seen" warning and the
 * not-yet-decided count. Never blocks the screen: a failure here costs a warning, not the
 * ability to edit, so it resolves to [] rather than throwing.
 */
export const listPickList = async config => {
  try {
    const response = await API.get(API_NAME, config.pickList.endpoint);
    return response?.response_body ?? [];
  } catch (e) {
    console.log('listPickList error', e);
    return [];
  }
};

export const createRow = async (config, row) => {
  try {
    const response = await API.post(API_NAME, config.endpoint, { body: row });
    return response?.response_body;
  } catch (e) {
    console.log('createRow error', e);
    throw new Error(messageFrom(e, 'Failed to add the row. Please try again.'));
  }
};

// These tables have no surrogate key, so a row is addressed by its natural key. Sending a
// different key in the body moves it -- that is how a misspelling gets corrected -- and the
// proc duplicate-checks the destination first.
export const updateRow = async (config, row, origKey) => {
  try {
    const response = await API.put(API_NAME, config.endpoint, {
      body: { ...row, [`Orig${config.keyField}`]: origKey },
    });
    return response?.response_body;
  } catch (e) {
    console.log('updateRow error', e);
    throw new Error(messageFrom(e, 'Failed to save the row. Please try again.'));
  }
};

export const deleteRow = async (config, key) => {
  try {
    const response = await API.del(API_NAME, config.endpoint, {
      queryStringParameters: { [config.keyField]: key },
    });
    return response?.response_body;
  } catch (e) {
    console.log('deleteRow error', e);
    throw new Error(messageFrom(e, 'Failed to delete the row. Please try again.'));
  }
};

// Bulk import. The API validates every row before writing anything, so a rejected import
// changes nothing -- the per-row failures come back in response_body.errors for display.
// Throws an Error carrying a `rows` property in that case.
export const importRows = async (config, { mode, rows }) => {
  try {
    const response = await API.post(API_NAME, config.importEndpoint, {
      body: { Mode: mode, Rows: rows },
    });
    return response?.response_body;
  } catch (e) {
    console.log('importRows error', e);
    const error = new Error(messageFrom(e, 'The import failed. Nothing was changed.'));
    error.rows = e?.response?.data?.response_body?.errors ?? [];
    throw error;
  }
};

// One audit endpoint serves every code table; TableName selects which.
export const listAudit = async (config, { keyValue } = {}) => {
  try {
    const queryStringParameters = { TableName: config.tableName };
    if (keyValue) {
      queryStringParameters.KeyValue = keyValue;
    }
    const response = await API.get(API_NAME, '/code-table-audit', { queryStringParameters });
    return response?.response_body ?? [];
  } catch (e) {
    console.log('listAudit error', e);
    throw new Error(messageFrom(e, 'Failed to load the change history. Please try again later.'));
  }
};
